<template>
	<Provider>
		<component
			:is="layoutComponent"
			id="app-layout"
			:class="[
				`theme-${themeName}`,
				`layout-${layoutComponentName}`,
				`route-${routeName}`,
				{ 'opacity-0': loading }
			]"
		>
			<RouterView v-slot="{ Component: RouterComponent }">
				<transition :name="`router-${routerTransition}`" mode="out-in" appear>
					<component :is="RouterComponent" id="app-page" :key="routeName + forceRefresh" />
				</transition>
			</RouterView>
		</component>

		<GlobalComponents />
	</Provider>
</template>

<script lang="ts" setup>
import type { Component } from "vue"
import type { RouteLocationNormalized } from "vue-router"
import type { Layout, RouterTransition, ThemeNameEnum } from "@/types/theme"
import { computed, onBeforeMount, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import Blank from "@/app-layouts/Blank"
import Provider from "@/app-layouts/common/Provider.vue"
import HorizontalNav from "@/app-layouts/HorizontalNav"
import VerticalNav from "@/app-layouts/VerticalNav"
import GlobalComponents from "@/components/global/GlobalComponents.vue"
import { useFirebase } from "@/composables/common/useFirebase"
import { useAuthStore } from "@/stores/auth"
import { useMainStore } from "@/stores/main"
import { useNotificationsStore } from "@/stores/notifications"
import { useStaticStore } from "@/stores/static"
import { useThemeStore } from "@/stores/theme"
import { useMobileAppBanner } from "./composables/common/useMobileAppBanner"
import { useCookieConsentStore } from "./stores/cookieConsent"
import { useOfficesStore } from "./stores/offices"

const router = useRouter()
const route = useRoute()
const loading = ref(true)

const layoutComponents = {
	VerticalNav,
	HorizontalNav,
	Blank
}

const { open: openMobileAppBanner } = useMobileAppBanner()
const themeStore = useThemeStore()
const { initAnalytics, logPageView } = useFirebase()
const cookieConsentStore = useCookieConsentStore()
const mainStore = useMainStore()
const authStore = useAuthStore()
// TODO-NEXT: attivare socket store
// const socketStore = useSocketStore()
const notificationsStore = useNotificationsStore()
const staticStore = useStaticStore()
const officesStore = useOfficesStore()

authStore.ensureFingerprint()

const routeName = computed<string>(() => route?.name?.toString() || "")
const forceRefresh = computed<number>(() => mainStore.forceRefresh)
const forceLayout = ref<Layout | null>(null)
const layout = computed<Layout>(() => themeStore.layout)
const layoutComponentName = computed<Layout>(() => forceLayout.value || layout.value)
const layoutComponent = computed<Component>(() => layoutComponents[layoutComponentName.value])
const routerTransition = computed<RouterTransition>(() => themeStore.routerTransition)
const themeName = computed<ThemeNameEnum>(() => themeStore.themeName)
const isLogged = computed(() => authStore.isLogged)
const isAnalyticsAccepted = computed(() => cookieConsentStore.analyticsAccepted)

function checkThemeOverrides(currentRoute: RouteLocationNormalized) {
	if (currentRoute.meta?.theme?.layout !== undefined) {
		forceLayout.value = currentRoute.meta.theme.layout
	} else {
		forceLayout.value = null
	}
}

watch(layoutComponentName, () => {
	loading.value = false
})

watch(
	isLogged,
	val => {
		if (val) {
			// TODO-NEXT: attivare socket store
			// socketStore.init()
			notificationsStore.getList()
			authStore.loadProfile()
			officesStore.fetchOffices()
			staticStore.loadAll({ logged: val })
		}
	},
	{ immediate: true }
)

router.afterEach(currentRoute => {
	checkThemeOverrides(currentRoute)
	logPageView(currentRoute)
})

onBeforeMount(() => {
	staticStore.loadAll({ logged: isLogged.value })

	// Inizializza analytics solo se l'utente ha già accettato i cookie in una visita precedente
	if (isAnalyticsAccepted.value === true) {
		initAnalytics()
	}

	setTimeout(() => {
		loading.value = false
	}, 500)
})

onMounted(() => {
	setTimeout(() => {
		if (routeName.value !== "OpenApp" && routeName.value !== "NotFound") {
			openMobileAppBanner()
		}
	}, 500)
})
</script>
